import React from 'react';

import { Box, Container, Divider, Typography } from '@mui/material';

import { lightPalette } from '@app/theme';
import { InvestmentReportHero } from '@components/ui/InvestmentReportHero';

import { AnalysisSectionCard } from './AnalysisSectionCard';
import { DealBreakerTable } from './DealBreakerTable';
import { DueDiligenceSection } from './DueDiligenceSection';
import { InvestmentThesisBanner } from './InvestmentThesisBanner';
import { KeyMetricsTable } from './KeyMetricsTable';
import { ReportSidebar } from './ReportSidebar';
import { RiskList } from './RiskList';
import { ScoreBreakdownChart } from './ScoreBreakdownChart';
import { StrengthsWeaknesses } from './StrengthsWeaknesses';

import type { AnalysisReportViewProperties } from './AnalysisReportView.types';


const SCROLL_MARGIN = '96px';

const SectionTitle: React.FC<{ children: React.ReactNode; id?: string; dataTest?: string }> = ({
  children,
  id,
  dataTest,
}) => (
  <Typography
    id={id}
    variant='h5'
    sx={{
      fontWeight: 700,
      letterSpacing: '-0.02em',
      mb: 3,
      mt: 6,
      scrollMarginTop: SCROLL_MARGIN,
    }}
    data-testid={dataTest}
  >
    {children}
  </Typography>
);

/**
Subsection header
*/
const SubsectionHeader: React.FC<{ title: string; id?: string }> = ({
  title,
  id,
}) => (
  <Typography
    id={id}
    sx={{
      fontSize: 16,
      fontWeight: 700,
      color: lightPalette.text.primary,
      letterSpacing: '-0.02em',
      mb: 2,
      scrollMarginTop: SCROLL_MARGIN,
    }}
  >
    {title}
  </Typography>
);

export const AnalysisReportView: React.FC<AnalysisReportViewProperties> = ({
  review,
  fallbackFileName,
  onMaterialsDownload,
  dataTest = 'analysis_report',
  sx,
}) => {
  const { summary, scores, meta } = review;
  const sections = review.sections ?? [];
  const team_members = review.team_members ?? [];
  const risks = review.risks ?? [];
  const deal_breakers = review.deal_breakers ?? [];
  const due_diligence = review.due_diligence;
  const key_metrics = summary.key_metrics ?? [];
  const strengths = summary.strengths ?? [];
  const critical_risks = summary.critical_risks ?? [];

  const sortedSections = [...sections].toSorted((a, b) => a.order - b.order);

  return (
    <Box data-testid={dataTest} sx={{ py: { xs: 4, md: 6 }, ...sx }}>
      <Container maxWidth='lg'>
        {/* === Hero === */}
        <InvestmentReportHero
          projectName={meta.company_name}
          analysisDate={meta.analysis_date}
          fileName={meta.source_filename ?? fallbackFileName}
          onMaterialsDownload={onMaterialsDownload}
          dataTest={`${dataTest}_hero`}
        />

        {/* === Content: sidebar + main === */}
        <Box
          sx={{
            mt: 6,
            display: 'flex',
            gap: 4,
            alignItems: 'flex-start',
          }}
        >
          {/* Sidebar navigation (lg+ screens) */}
          <ReportSidebar
            review={review}
            dataTest={`${dataTest}_sidebar`}
          />

          {/* Main content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* === Резюме — инвестиционный тезис === */}
            <Box id='report-thesis' sx={{ scrollMarginTop: SCROLL_MARGIN }}>
              <InvestmentThesisBanner
                summary={summary}
                dataTest={`${dataTest}_thesis`}
              />
            </Box>

            {/* === Подсекция: Ключевые метрики === */}
            {key_metrics.length > 0 && (
              <Box id='report-metrics' sx={{ mt: 4, scrollMarginTop: SCROLL_MARGIN }}>
                <SubsectionHeader title='Ключевые метрики' />
                <KeyMetricsTable
                  metrics={key_metrics}
                  dataTest={`${dataTest}_metrics`}
                />
              </Box>
            )}

            {/* === Подсекция: Сильные и слабые стороны === */}
            {(strengths.length > 0 || critical_risks.length > 0) && (
              <Box id='report-sw' sx={{ mt: 4, scrollMarginTop: SCROLL_MARGIN }}>
                <SubsectionHeader title='Сильные и слабые стороны' />
                <StrengthsWeaknesses
                  strengths={strengths}
                  risks={critical_risks}
                  dataTest={`${dataTest}_sw`}
                />
              </Box>
            )}

            {/* === Секции анализа === */}
            {sortedSections.length > 0 && (
              <>
                <Divider sx={{ my: 5 }} />

                <SectionTitle dataTest={`${dataTest}_sections_title`}>
                  Детальный анализ
                </SectionTitle>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {sortedSections.map((section) => (
                    <AnalysisSectionCard
                      key={section.id}
                      section={section}
                      sectionNumber={section.order}
                      teamMembers={section.id === 'team' ? team_members : undefined}
                      dataTest={`${dataTest}_section_${section.id}`}
                    />
                  ))}
                </Box>
              </>
            )}

            {/* === Оценки === */}
            <Divider sx={{ my: 5 }} />
            <SectionTitle id='report-scores' dataTest={`${dataTest}_scores_title`}>
              Итоговая оценка
            </SectionTitle>
            <ScoreBreakdownChart
              scores={scores}
              dataTest={`${dataTest}_scores`}
            />

            {/* === Риски === */}
            {risks.length > 0 && (
              <>
                <Divider sx={{ my: 5 }} />
                <SectionTitle id='report-risks' dataTest={`${dataTest}_risks_title`}>
                  Риски
                </SectionTitle>
                <RiskList
                  risks={risks}
                  dataTest={`${dataTest}_risks`}
                />
              </>
            )}

            {/* === Deal Breakers === */}
            {deal_breakers.length > 0 && (
              <>
                <Divider sx={{ my: 5 }} />
                <SectionTitle id='report-deal-breakers' dataTest={`${dataTest}_deal_breakers_title`}>
                  Критерии Go / No-Go
                </SectionTitle>
                <DealBreakerTable
                  dealBreakers={deal_breakers}
                  dataTest={`${dataTest}_deal_breakers`}
                />
              </>
            )}

            {/* === Due Diligence === */}
            {due_diligence &&
              Object.values(due_diligence).some(
                (questions) => Array.isArray(questions) && questions.length > 0,
              ) && (
              <>
                <Divider sx={{ my: 5 }} />
                <SectionTitle id='report-dd' dataTest={`${dataTest}_dd_title`}>
                  Вопросы для Due Diligence
                </SectionTitle>
                <DueDiligenceSection
                  dueDiligence={due_diligence}
                  dataTest={`${dataTest}_dd`}
                />
              </>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};
